import { gql } from "lib/apolloClient";
import { userNameFragment } from "modules/CommonFragments";

export const getTranscriptCommentFieldsQuery = gql`
  query getTranscriptCommentFields($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        transcriptCommentFieldsV3 {
          id
        }
      }
    }
  }
`;

export const getStudentTranscriptCommentQuery = gql`
  query getStudentTranscriptComment(
    $studentId: ID!
    $filters: TranscriptStudentCommentFiltersV3
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        transcriptSummary {
          id
          comments(filters: $filters) {
            id
            value
            updatedAt
            commentField {
              id
            }
          }
        }
      }
    }
  }
`;

export const getStudentPersonalDetailsQuery = gql`
  query getStudentPersonalDetails($id: ID!) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        transcriptSummary {
          id
          basicDetails {
            id
            lastSyncAt
            fields {
              id
              fieldUid
              sourceValue
              value
            }
          }
        }
      }
    }
  }
`;

export const getStudentCustomFieldConfigQuery = gql`
  query getStudentCustomFieldConfig(
    $id: ID!
    $filter: GlobalConstantsFilterOptions
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        globalConstants(filter: $filter) {
          id
          name
          value
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

// Academic records queries
export const getStudentTranscriptAcademicRecordsQuery = gql`
  query getStudentTranscriptAcademicRecords($id: ID!) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        id
        transcriptSummary {
          id
          configuration
          grades {
            id
            displaySequence
            student {
              id
            }
            grade {
              id
              name
            }
            dataSource
            school {
              id
              name
              sourceId
              source {
                ... on GenericCurriculumProgram {
                  id
                  academicSetupType
                }
              }
            }
            academicYear {
              id
              label
              sourceId
            }
            lastSyncBy {
              id
              firstName
              lastName
            }
            lastSyncAt
            lastUpdatedBy {
              id
              firstName
              lastName
            }
            lastUpdatedAt
            gradingPeriods {
              gradingPeriod {
                id
                name
              }
              displaySequence
              isEnabled
            }
            courses {
              course {
                id
                name
                sourceId
              }
              includeInGpa
              level {
                id
                name
              }
              displaySequence
              courseCode
              sourceCourseCode
              sourceLevel {
                id
              }
            }
            configuration
            ratingSummary {
              ratings {
                id
                studentGradeMapId
                gradingPeriodId
                courseId
                gradeScaleId
                sourceValue
                value
              }
            }
            gpaSettings {
              gpaCalculationMethod
              gpaScoreRoundFormat
              gpaDecimalPrecision
              maxGpaValue
              gradePointCalculationMethod
              gradePointConversionMethod
              gradePointScoreRoundFormat
              gradePointDecimalPrecision
              gradePointConversion
              gradePointScale
            }
          }
        }
      }
    }
  }
`;

export const getTranscriptResourcesQuery = gql`
  query getTranscriptResources($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        transcriptGradeScales(filters: { types: ["CREDIT", "GRADE_POINT"] }) {
          id
          type
        }
        transcriptCourses {
          id
          name
          displaySequence
          includeInGpa
          sourceType
          source {
            ... on LearningCourse {
              id
              curriculumProgram {
                id
              }
              grades {
                id
                globalGrade {
                  id
                  name
                }
              }
            }
            ... on Subject {
              id
              curriculumProgram {
                id
              }
              grades {
                id
                globalGrade {
                  id
                  name
                }
              }
            }
          }
          associations {
            totalCount
          }
        }
        transcriptLevels {
          id
          name
          displaySequence
          source {
            ... on GenericTag {
              id
              curriculumProgram {
                id
              }
            }
          }
          associations {
            totalCount
          }
        }
      }
    }
  }
`;

export const getOrgTranscriptEntitiesQuery = gql`
  query getOrgTranscriptEntitiesQuery($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        curriculumPrograms {
          id
          gradeScale {
            edges {
              node {
                id
                displayName
                type
              }
            }
          }
        }
        transcriptGradingPeriods {
          id
          name
          isCustom
          displaySequence
          associations {
            totalCount
          }
          source {
            ... on AcademicTerm {
              id
              academicYear {
                id
              }
              curriculumProgram {
                id
              }
            }
          }
        }
        transcriptGradeScales(
          filters: {
            excludedTypes: [
              "CREDIT"
              "FINAL_SCORE"
              "FINAL_GRADE"
              "GRADE_POINT"
              "QUALITY_POINT"
            ]
          }
        ) {
          id
          name
          isCustom
          displaySequence
          source {
            ... on GradeScale {
              id
              displayName
            }
          }
          associations {
            totalCount
          }
        }
      }
    }
  }
`;

export const getStudentYearGroup = gql`
  query getStudentYearGroup($id: ID!, $filters: GroupStudentFilter) {
    node(id: $id, type: YEAR_GROUP) {
      id
      ... on YearGroup {
        students(filters: $filters) {
          edges {
            node {
              id
              ...userNameFields
              profileImage
              sourceId
            }
          }
        }
      }
    }
  }
  ${userNameFragment}
`;
